/**
 * Custom hook for NCR Edit state management
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  qualityAPI,
  platformSettingsAPI,
  coreAPI,
  NonConformanceReport,
  NCRDocument,
  NCRComment,
  NCRActionLog,
  UserProfile,
  SourceOfRejection,
  FaultCodeGroup,
  FaultCodeCategory,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { NCREditFormData, ProgressStep } from './types';
import { RCA_REQUIRED_FIELDS, CAR_REQUIRED_FIELDS } from './constants';

export function useNCREdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Data states
  const [ncr, setNcr] = useState<NonConformanceReport | null>(null);
  const [documents, setDocuments] = useState<NCRDocument[]>([]);
  const [comments, setComments] = useState<NCRComment[]>([]);
  const [actionLogs, setActionLogs] = useState<NCRActionLog[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [supplierUsers, setSupplierUsers] = useState<UserProfile[]>([]);

  // Classification dropdown options
  const [sourcesOfRejection, setSourcesOfRejection] = useState<SourceOfRejection[]>([]);
  const [faultGroups, setFaultGroups] = useState<FaultCodeGroup[]>([]);
  const [faultCategories, setFaultCategories] = useState<FaultCodeCategory[]>([]);

  // Form states
  const [formData, setFormData] = useState<NCREditFormData>({});
  const [newComment, setNewComment] = useState('');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  // Template type: 1 = Mandatory 8D (default), 2 = Optional 8D
  // Fetched from platform settings
  const [ncrTemplate, setNcrTemplate] = useState<1 | 2>(1);

  // Template 2 configuration - prefer formData (live edits) over saved ncr data
  const is8DRequired = (formData.is_8d_required as boolean | undefined) ?? ncr?.is_8d_required ?? true;
  const isCollectionRequired = (formData.is_collection_required as boolean | undefined) ?? ncr?.is_collection_required ?? false;
  const isCreditRequired = (formData.is_credit_required as boolean | undefined) ?? ncr?.is_credit_required ?? false;
  const liabilityType = (formData.liability_type as number | undefined) ?? ncr?.liability_type ?? 0;

  // Get user role from auth context
  const { user } = useAuth();
  const userRole = user?.company_type || user?.platform?.toLowerCase() || 'supplier';
  const isSupplier = userRole === 'supplier' || userRole === 'helix';
  const isCustomer = userRole === 'customer';

  // Workflow permissions
  const canEditStep1 = isSupplier && ncr?.status === 'draft';
  const canEditStep2 = isCustomer && ncr?.status === 'ca_submitted';
  const canEditStep3 = isCustomer && ncr?.status === 'car_submitted';

  // Generate Template 2 progress steps
  const getTemplate2Steps = (): ProgressStep[] => {
    const liabilityDetermined = liabilityType > 0;
    const liabilityLabel = liabilityType === 1 ? 'Supplier Liability' :
      liabilityType === 2 ? 'Customer Liability' :
      liabilityType === 3 ? 'Shared Liability' :
      liabilityType === 4 ? 'No Liability' : 'Liability';

    const steps: ProgressStep[] = [
      {
        title: 'Created',
        icon: '📋',
        state: currentStep >= 1 ? 'completed' : 'pending',
      },
      {
        title: liabilityDetermined ? liabilityLabel : 'Liability',
        icon: liabilityType === 1 ? '🏭' : liabilityType === 2 ? '🏢' : '⚖️',
        state: liabilityDetermined ? 'completed' : 'pending',
      },
    ];

    if (is8DRequired) {
      steps.push({
        title: '8D Completed',
        icon: '⚠️',
        state: currentStep >= 3 ? 'completed' : 'pending',
      });
    }

    if (isCollectionRequired) {
      const collectionDone = ncr?.collection_completed || ncr?.collection_process?.status === 'completed';
      steps.push({
        title: 'Collection',
        icon: '📦',
        state: collectionDone ? 'completed' : 'pending',
      });
    }

    if (isCreditRequired) {
      const creditDone = ncr?.credit_completed || ncr?.credit_process?.status === 'processed';
      steps.push({
        title: 'Credit',
        icon: '💰',
        state: creditDone ? 'completed' : 'pending',
      });
    }

    steps.push({
      title: 'Closed',
      icon: '✅',
      state: currentStep >= 4 ? 'completed' : 'pending',
    });

    return steps;
  };

  // Fetch platform settings to get NCR template preference
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        const response = await platformSettingsAPI.getSettings();
        const template = response.data.ncr_template;
        if (template === 1 || template === 2) {
          setNcrTemplate(template);
        }
      } catch (error) {
        console.error('Error fetching platform settings:', error);
        // Default to template 1 if fetch fails
      }
    };
    const fetchClassificationOptions = async () => {
      try {
        const [sorResp, fgResp, fcResp] = await Promise.all([
          qualityAPI.getSourcesOfRejection({ is_active: true, page_size: 200 }),
          qualityAPI.getFaultGroups({ is_active: true, page_size: 200 }),
          qualityAPI.getFaultCategories({ is_active: true, page_size: 500 }),
        ]);
        setSourcesOfRejection(sorResp.data.results);
        setFaultGroups(fgResp.data.results);
        setFaultCategories(fcResp.data.results);
      } catch {
        console.error('Error fetching classification options');
      }
    };

    fetchPlatformSettings();
    fetchClassificationOptions();
  }, []);

  // Fetch NCR details
  useEffect(() => {
    if (id) {
      fetchNCRDetails();
    }
  }, [id]);

  const fetchNCRDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [ncrResponse, docsResponse, commentsResponse, logsResponse] = await Promise.all([
        qualityAPI.getNCR(parseInt(id)),
        qualityAPI.getNCRDocuments(parseInt(id)),
        qualityAPI.getNCRComments(parseInt(id)),
        qualityAPI.getNCRActionLogs(parseInt(id)),
      ]);

      const ncrData = ncrResponse.data;
      setNcr(ncrData);
      setFormData(ncrData as NCREditFormData);
      setDocuments(docsResponse.data.results);
      setComments(commentsResponse.data.results);
      setActionLogs(logsResponse.data.results);

      // Fetch supplier site users for buyer dropdown
      if (ncrData.company) {
        try {
          const usersResponse = await coreAPI.getCompanyUsers(ncrData.company);
          setSupplierUsers(usersResponse.data);
        } catch {
          console.error('Error fetching customer users');
        }
      }

      // Determine current step based on status
      if (ncrData.status === 'draft') setCurrentStep(1);
      else if (ncrData.status === 'ca_submitted') setCurrentStep(2);
      else if (ncrData.status === 'car_submitted') setCurrentStep(3);
      else if (ncrData.status === 'closed') setCurrentStep(4);
    } catch (error) {
      console.error('Error fetching NCR details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;

    try {
      setSaving(true);
      // Build patch payload from editable fields
      const payload: Record<string, unknown> = {};
      const editableFields = [
        'grn_number', 'po_number', 'part_number', 'description',
        'batch_number', 'works_order', 'reject_qty', 'batch_qty',
        'unit_price', 'shortage_qty', 'severity', 'defect_description',
        'buyer_name', 'liability_type', 'liability_classification',
        'is_8d_required', 'is_collection_required', 'is_credit_required',
        'proposed_rejection_type', 'disposition_quantity', 'action_required',
        'source_of_rejection_option', 'fault_group', 'fault_category',
      ];
      for (const field of editableFields) {
        if (formData[field] !== undefined) {
          payload[field] = formData[field];
        }
      }
      await qualityAPI.updateNCR(parseInt(id), payload as Partial<NonConformanceReport>);
      toast.success('Saved successfully');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error saving NCR:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitDetail = async () => {
    if (!id) return;

    try {
      setSaving(true);
      // Save all form data first before transitioning status
      const payload: Record<string, unknown> = {};
      const editableFields = [
        'grn_number', 'po_number', 'part_number', 'description',
        'batch_number', 'works_order', 'reject_qty', 'batch_qty',
        'unit_price', 'shortage_qty', 'severity', 'defect_description',
        'buyer_name', 'liability_type', 'liability_classification',
        'is_8d_required', 'is_collection_required', 'is_credit_required',
        'proposed_rejection_type', 'disposition_quantity', 'action_required',
        'source_of_rejection_option', 'fault_group', 'fault_category',
      ];
      for (const field of editableFields) {
        if (formData[field] !== undefined) {
          payload[field] = formData[field];
        }
      }
      await qualityAPI.updateNCR(parseInt(id), payload as Partial<NonConformanceReport>);
      await qualityAPI.submitDetail(parseInt(id));
      toast.success('Detail submitted successfully');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error submitting detail:', error);
      toast.error('Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRCA = async () => {
    if (!id || !formData) return;

    const missing = RCA_REQUIRED_FIELDS.filter(
      (field) => !formData[field as keyof typeof formData]
    );

    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      await qualityAPI.submitRCA(parseInt(id), {
        why_1: formData.why_1 || '',
        why_2: formData.why_2 || '',
        why_3: formData.why_3 || '',
        why_4: formData.why_4 || '',
        why_5: formData.why_5 || '',
        selected_root_cause: formData.selected_root_cause || '',
        immediate_corrective_action: formData.immediate_corrective_action || '',
        disposition_instructions: formData.disposition_instructions,
      });
      toast.success('Root Cause Analysis submitted successfully');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error submitting RCA:', error);
      toast.error('Failed to submit RCA');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitCAR = async () => {
    if (!id || !formData) return;

    const missing = CAR_REQUIRED_FIELDS.filter(
      (field) => !formData[field as keyof typeof formData]
    );

    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      await qualityAPI.submitCAR(parseInt(id), {
        corrective_action: formData.corrective_action || '',
        preventive_action: formData.preventive_action || '',
        impact_assessment: formData.impact_assessment || '',
        impact_on_similar_products: formData.impact_on_similar_products || '',
        similar_products_affected: formData.similar_products_affected || false,
        training_required: formData.training_required || false,
        training_details: formData.training_details,
        doc_process_flow_updated: formData.doc_process_flow_updated || false,
        doc_control_plan_updated: formData.doc_control_plan_updated || false,
        doc_fmea_updated: formData.doc_fmea_updated || false,
        doc_work_instructions_updated: formData.doc_work_instructions_updated || false,
        doc_other_updated: formData.doc_other_updated || '',
        documentation_references: formData.documentation_references || '',
        corrective_action_notes: formData.corrective_action_notes || '',
      });
      toast.success('Corrective Action Report submitted successfully');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error submitting CAR:', error);
      toast.error('Failed to submit CAR');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!id || !newComment.trim()) return;

    try {
      await qualityAPI.addNCRComment(parseInt(id), newComment, false);
      setNewComment('');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleClose = async () => {
    if (!id) return;

    try {
      await qualityAPI.closeNCR(parseInt(id));
      toast.success('NCR closed successfully');
      setCloseDialogOpen(false);
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error closing NCR:', error);
      toast.error('Failed to close NCR');
    }
  };

  const handleConfirmCollection = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.confirmCollection(parseInt(id));
      toast.success('Collection confirmed');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error confirming collection:', error);
      toast.error('Failed to confirm collection');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCredit = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.confirmCredit(parseInt(id));
      toast.success('Credit confirmed');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error confirming credit:', error);
      toast.error('Failed to confirm credit');
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Collection Process handlers
  const handleInitiateCollection = async (data: {
    proposed_collection_date?: string;
    collection_address?: string;
    collection_notes?: string;
    supplier_notes?: string;
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateCollection(parseInt(id), data);
      toast.success('Collection process initiated');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error initiating collection:', error);
      toast.error('Failed to initiate collection');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCollectionStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.updateCollectionStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateCollectionStatus>[1]);
      toast.success(`Collection ${newStatus === 'completed' ? 'completed' : 'updated'}`);
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error updating collection status:', error);
      toast.error('Failed to update collection');
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Credit Process handlers
  const handleInitiateCredit = async (data: {
    credit_amount?: number;
    credit_currency?: string;
    credit_reason?: string;
    supplier_notes?: string;
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateCredit(parseInt(id), data);
      toast.success('Credit process initiated');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error initiating credit:', error);
      toast.error('Failed to initiate credit');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCreditStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.updateCreditStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateCreditStatus>[1]);
      const labels: Record<string, string> = {
        under_review: 'Credit moved to review',
        approved: 'Credit approved',
        rejected: 'Credit rejected',
        requested: 'Credit re-requested',
        processed: 'Credit processed',
      };
      toast.success(labels[newStatus] || 'Credit updated');
      await fetchNCRDetails();
    } catch (error) {
      console.error('Error updating credit status:', error);
      toast.error('Failed to update credit');
    } finally {
      setSaving(false);
    }
  };

  const canClose = (() => {
    if (!ncr || ncr.status !== 'car_submitted') return false;
    const collectionDone = ncr.collection_completed || ncr.collection_process?.status === 'completed';
    const creditDone = ncr.credit_completed || ncr.credit_process?.status === 'processed';
    if (ncr.is_collection_required && !collectionDone) return false;
    if (ncr.is_credit_required && !creditDone) return false;
    return true;
  })();

  const updateFormData = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const goBack = () => navigate('/quality');

  return {
    // Data
    ncr,
    documents,
    comments,
    actionLogs,
    formData,

    // UI states
    loading,
    saving,
    currentStep,
    ncrTemplate,
    newComment,
    closeDialogOpen,

    // Permissions
    isSupplier,
    isCustomer,
    canEditStep1,
    canEditStep2,
    canEditStep3,

    // Setters
    setNewComment,
    setCloseDialogOpen,
    updateFormData,

    // Supplier users for buyer dropdown
    supplierUsers,

    // Classification options
    sourcesOfRejection,
    faultGroups,
    faultCategories,

    // Template 2 config
    liabilityType,
    is8DRequired,
    isCollectionRequired,
    isCreditRequired,

    // Actions
    refreshData: fetchNCRDetails,
    goBack,
    handleSave,
    handleSubmitDetail,
    handleSubmitRCA,
    handleSubmitCAR,
    handleAddComment,
    handleClose,
    handleConfirmCollection,
    handleConfirmCredit,
    getTemplate2Steps,

    // Multi-step workflow actions
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleInitiateCredit,
    handleUpdateCreditStatus,

    // Computed
    canClose,
  };
}
